import {
    calculateAverageSectionScore,
    calculateAverageSourceScore,
} from '../../base/scoreHelper';
import { ReportChapterObject, ReportSectionContent } from '../../base/types';
import * as CaseStudies from '../../content/case-study';
import { orderBy } from 'lodash';

const sections: ReportSectionContent[] = [
    {
        id: '1',
        title: `AI-powered conversion from Enzyme to React Testing Library at Slack`,
        caption: `Slack - Test Migration`,
        content: [
            {
                kind: 'text',
                text: `Slacks case study highlights a forward-thinking approach to solving legacy migration challenges by effectively combining traditional programming techniques with cutting-edge AI. The strategic shift to a hybrid model that leverages both AST transformations and LLM-generated insights not only accomplished a high conversion success rate but also significantly improved the daily productivity of the development teams. Through diligent evaluation and continuous refinement, Slack has demonstrated that with the right blend of technology and human oversight, even the most complex migration projects can yield substantial time savings and enhanced product quality.`,
            },
        ],
        keywords: [],
        sources: [CaseStudies.slackCaseStudy],
    },
    {
        id: '2',
        title: `GenAI streamlines maintenance software development`,
        caption: `TikTok - Code Maintenance`,
        content: [
            {
                kind: 'text',
                text: `This case study highlights how a leading global video platform has successfully integrated GenAI into its software development lifecycle. By combining public GenAI models with tailored internal solutions—and incorporating advanced features such as automated Jira ticketing—the company has achieved significant productivity gains, enhanced code quality, and streamlined maintenance processes, all while effectively navigating challenges related to data security and legacy code integration. Varos' private interview provided in-depth insights into both the successes and hurdles of this innovative approach.`,
            },
        ],
        keywords: [],
        sources: [CaseStudies.tiktokCaseStudy],
    },
    // Add Airbnb Case Study Section
    {
        id: 'airbnb-case-study',
        title: `GenAI accelerates large-scale test migration at Airbnb`,
        caption: `Airbnb - Test Migration`,
        content: [
            {
                kind: 'text',
                text: `This case study illustrates how Airbnb harnessed generative AI to not only accelerate a major code migration but also to significantly enhance developer productivity by automating repetitive, large-scale refactoring tasks while maintaining high code quality and test coverage.`,
            },
        ],
        keywords: [],
        sources: [CaseStudies.airbnbCaseStudy], // Reference the imported fixture
    },

    // End Example Blog Section
    {
        id: 'uber-autocover',
        title: 'Uber AutoCover: AI Agents for Unit Test Generation',
        caption: 'Uber - Test Generation',
        content: [
            {
                kind: 'text',
                text: "Uber's AutoCover project leverages a sequence of AI agents (Preparer, Generator, Executor, Fixer, Refactor, Validator) built on GitHub Copilot and VS Code extensibility to automate unit test generation. This agentic system tackles enterprise codebase complexity by incorporating deterministic checks, mutation testing, and feedback loops. The result is a significant reduction in developer toil (automating a 29-step process), drastically improved code coverage (demonstrated achieving 100%), and increased confidence in test quality, allowing developers to focus on core application logic.",
            },
        ],
        keywords: [],
        sources: [CaseStudies.uberAutocoverCaseStudy],
    },
    {
        id: 'transunion',
        title: 'TransUnion Automates Log Onboarding for Engineering Efficiency',
        caption: 'TransUnion - Log Automation',
        content: [
            {
                kind: 'text',
                text: 'TransUnion revolutionized its log onboarding process, reducing a task that took days down to minutes. Leveraging a suite of tools including Splunk, Rundeck, Bitbucket, AWS, and Cribl, they built a self-service model. This automation, inspired by GenAI workflow optimization principles, standardized source types via a Cribble-based ledger, significantly reduced manual engineering effort (by 25% in one PI), and freed developers to focus on business value instead of complex log configuration management.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.transunionCaseStudy],
    },
    {
        id: 'airbnb-dev-platform',
        title: 'Airbnb: Balancing Innovation and Practicality with GenAI Tools',
        caption: 'Airbnb - Developer Tools',
        content: [
            {
                kind: 'text',
                text: "Airbnb's experience with integrating GenAI tools like ChatGPT, GitHub Copilot, and their internal One Chat system reveals significant productivity gains balanced with the realities of technical limitations and security concerns. They leverage AI for tasks ranging from boilerplate code generation and documentation to advanced code reviews via hackathon prototypes. While Copilot adoption correlated with a 5% increase in PR frequency, challenges remain in handling complex refactoring and ensuring code quality. Airbnb manages these by fostering innovation through frameworks like Sandcastle, implementing security measures like internal chat tools, and carefully managing costs associated with token usage.",
            },
        ],
        keywords: [],
        sources: [CaseStudies.airbnbDevPlatformCaseStudy],
    },
    {
        id: 'att',
        title: "AT&T's Secure AI Platform for Software Development",
        caption: 'AT&T - Secure Platform',
        content: [
            {
                kind: 'text',
                text: 'AT&T has implemented a comprehensive AI-driven platform that automates the entire software development lifecycle while ensuring enterprise-grade security. Their solution combines RAG architecture with intelligent routing to provide secure, role-based access to AI capabilities. The platform has been widely adopted across the organization, with over 140 distinct domain-based pipelines covering areas like HR, Finance, and Network Security. Key results include automated code generation with a 33% acceptance rate, faster employee query resolution, and enhanced security compliance throughout the development process.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.attCaseStudy],
    },
    {
        id: 'cisco',
        title: "Cisco's AI-Powered Code Migration and Testing",
        caption: 'Cisco - Code Migration',
        content: [
            {
                kind: 'text',
                text: 'Cisco has implemented a comprehensive AI-driven approach to modernize their codebase, focusing on automated migration and testing. Their system combines static analysis with generative AI to transform legacy code into modern architectures while maintaining functionality and improving test coverage. The results have shown significant improvements in development velocity and code quality, with automated test generation reducing manual testing efforts by over 60%.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.ciscoCaseStudy],
    },
    {
        id: 'uber-genie',
        title: "Uber's Genie: Transforming On-Call Support with AI",
        caption: 'Uber - On-Call Support',
        content: [
            {
                kind: 'text',
                text: "Uber's Genie project showcases a sophisticated implementation of generative AI in developer operations, specifically targeting the challenges of on-call support. By creating an AI copilot that understands and processes internal documentation, Genie demonstrates how large language models can be effectively integrated into existing workflows to solve real operational challenges. The implementation leverages a sophisticated RAG (Retrieval Augmented Generation) system for accessing and contextualizing internal documentation, integrated with existing incident management tools and documentation systems. The results have been significant, including reduced mean time to resolution for incidents, improved developer experience during on-call rotations, and more efficient access to relevant documentation and historical context.",
            },
        ],
        keywords: [],
        sources: [CaseStudies.uberGenieCaseStudy],
    },
    {
        id: 'monday-com',
        title: 'Monday.com Enhances Coding Efficiency with GenAI Tools',
        caption: 'Monday.com - Coding Efficiency',
        content: [
            {
                kind: 'text',
                text: 'Monday.com utilizes GenAI tools like Cursor-AI for auto-completion and a custom tool for pull request analysis to boost developer productivity. While requiring human oversight, Cursor-AI provides accurate code suggestions, enhancing coding speed by an estimated 25%. The PR analysis tool streamlines reviews by summarizing changes and suggesting improvements. This integration allows developers to focus more on innovation by automating routine coding and review tasks.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.mondayCaseStudy],
    },
    {
        id: 'goldman-sachs',
        title: 'Goldman Sachs Integrates GenAI Across the Development Lifecycle',
        caption: 'Goldman Sachs - Development Lifecycle',
        content: [
            {
                kind: 'text',
                text: 'Goldman Sachs leverages its internal GS AAI platform to safely integrate GenAI into the development workflow for its 12,000 engineers. Using techniques like RAG and embeddings, combined with partnerships (Microsoft, Google, Nvidia) and open-source models (Llama 3.1), they achieve 10-40% productivity gains in coding, testing, and documentation. This approach addresses accuracy, security, and regulatory challenges within the financial sector, allowing developers to focus on higher-value tasks.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.goldmanSachsCaseStudy],
    },
    {
        id: 'santander-uk',
        title: 'Santander UK Enhances Threat Modeling & DevSecOps with GenAI',
        caption: 'Santander UK - DevSecOps',
        content: [
            {
                kind: 'text',
                text: 'Santander UK leverages GenAI to boost DevSecOps efficiency, particularly in threat modeling. Using tools like stride GPT and a dual-persona (writer/editor) system for content generation, they automate tasks traditionally requiring security experts. The approach includes autonomous agent-based scanning orchestrated via Langchain, reducing task times from days to hours. While managing challenges like output variability and hallucinations with prompt engineering and human oversight, this integration streamlines security processes and improves consistency.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.santanderCaseStudy],
    },
    {
        id: 'capgemini',
        title: 'Capgemini Automates Java Unit Testing & Refactoring with GenAI',
        caption: 'Capgemini - Code Refactoring',
        content: [
            {
                kind: 'text',
                text: 'Capgemini leverages GenAI agents to tackle legacy Java code, automating unit test generation and code refactoring. Their system processes code class-by-class, using iterative feedback loops to correct compilation and assertion errors, boosting test coverage from ~3% to ~50%. It also refactors long or complex methods and generates functional documentation from source code. Running on private, modest hardware, this approach enhances productivity and provides developers psychological safety when working with fragile legacy systems.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.capgeminiCaseStudy],
    },
    {
        id: 'uber-query-gpt',
        title: 'Uber QueryGPT: GenAI for Natural Language SQL Queries',
        caption: 'Uber - SQL Generation',
        content: [
            {
                kind: 'text',
                text: 'Uber developed QueryGPT to convert natural language questions into SQL queries, reducing query writing time from ~10 minutes to ~3 minutes. The system uses a multi-agent RAG process (Intent Agent, Table Agent, Column Prune Agent) to handle complex schemas and avoid token limits. Despite challenges like LLM hallucinations, iterative development and evaluation have made QueryGPT a valuable tool for democratizing data access and boosting productivity for engineers and analysts.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.uberQueryGPTCaseStudy],
    },
    {
        id: 'rio-tinto',
        title: 'Rio Tinto Boosts Coder Productivity with GenAI Foundation Models',
        caption: 'Rio Tinto - Productivity Tools',
        content: [
            {
                kind: 'text',
                text: 'Rio Tinto leverages GenAI foundation models (like GPT-4) to enhance productivity in its engineering and software teams, achieving 20-30% gains. AI assists in debugging, refactoring, and translating legacy code (e.g., VB to TypeScript). It also helps synthesize insights from large datasets and historical documents, democratizing coding tasks. Challenges include domain specificity and data scarcity in heavy industry, requiring integration with established engineering knowledge and careful industrialization of use cases.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.rioTintoCaseStudy],
    },
    {
        id: 'citi-bank',
        title: 'Citi Bank Modernizes Legacy Systems and Boosts Productivity with GenAI',
        caption: 'Citi Bank - Legacy Modernization',
        content: [
            {
                kind: 'text',
                text: 'Citi employs GitHub Copilot Enterprise across its 40,000 developers, leveraging Retrieval-Augmented Generation (RAG) on its internal code repository. This enhances productivity by accelerating code generation and modernizing legacy systems (e.g., translating to Java). The controlled environment, while sometimes limiting, ensures compliance and reduces plagiarism risks. GenAI also assists in initial compliance assessment prep work, freeing up experts for deeper analysis.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.citiBankCaseStudy],
    },
    {
        id: 'td-bank',
        title: 'TD Bank Accelerates Developer Workflows with GitHub Copilot',
        caption: 'TD Bank - Workflow Acceleration',
        content: [
            {
                kind: 'text',
                text: 'TD Bank piloted GitHub Copilot with its engineering teams, finding significant productivity boosts. Survey results showed 50% of engineers could save up to 20 hours per sprint, and 93% of data engineers felt equally or more productive. The tool assists with code completion, unit testing, and answering coding questions via natural language, allowing engineers to focus on more complex tasks while maintaining security and compliance.',
            },
        ],
        keywords: [],
        sources: [CaseStudies.tdBankCaseStudy],
    },
    {
        id: 'sunlife',
        title: 'Sun Life Reinvents Legacy Systems with Strategic GenAI Adoption',
        caption: 'Sun Life - Legacy Transformation',
        content: [
            {
                kind: 'text',
                text: "Sun Life is using IBM's watsonX for mainframe modernization, converting legacy COBOL/PL/I code to modern languages and extracting historical business rules into natural language. They established early AI guardrails with cross-functional teams and partnered with CIFAR for enterprise-wide AI literacy training. This strategic approach balances innovation with risk management, mitigating knowledge loss from retiring talent while accelerating client initiatives.",
            },
        ],
        keywords: [],
        sources: [CaseStudies.sunlifeCaseStudy],
    },
    {
        id: 'jpmorgan',
        title: 'AWS-Integrated GenAI Drives Operational Efficiency at JPMorgan Chase',
        caption: 'JPMorgan Chase - Cloud Integration',
        content: [
            {
                kind: 'text',
                text: `Through a combination of internal innovation and strategic collaboration with AWS, JPMorgan Chase is successfully leveraging generative AI tools to enhance the productivity of its software developers and other business units. By deploying platforms like the LLM Suite and integrating solutions such as AWS SageMaker and Bedrock, the firm has not only improved operational efficiency and resiliency but has also built an agile, modern architecture that supports continuous innovation.`,
            },
        ],
        keywords: [],
        sources: [CaseStudies.jpmorganCaseStudy],
    },
];

export const implementations: Omit<ReportChapterObject, 'number'> = {
    id: 'implementations',
    caption: `Implementations`,
    title: `Companies Are Transforming Software Development and Maintenance with Specialized GenAI Solutions`,
    description: `Enterprises across finance, technology, and traditional industries are achieving 20-50% productivity gains by integrating GenAI throughout their development lifecycles. Financial institutions leverage GitHub Copilot and RAG-enhanced tools, while tech companies deploy specialized AI agents for testing, code migration, and incident response. The most successful implementations combine foundation models with company-specific context and maintain human oversight while balancing innovation with security requirements.`,
    editedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
    sections: orderBy(sections, calculateAverageSectionScore, 'desc'),
};
